import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="trix-link-dialog"
//
// Replaces Trix's default link dialog with a two-field popover (Text + URL).
// When the user clicks the Trix link toolbar button, this controller intercepts,
// reads any currently selected text, opens its own dialog, and on Apply inserts
// the text and applies the href attribute via the Trix editor API.
export default class extends Controller {
  static targets = ["popover", "text", "url", "remove"];

  connect() {
    this.editorElement = this.element.querySelector("trix-editor");
    if (!this.editorElement) return;

    this._onLinkButtonClick = this._onLinkButtonClick.bind(this);
    this._onDocClick = this._onDocClick.bind(this);
    this._onKey = this._onKey.bind(this);
    this._onTrixInitialize = this._onTrixInitialize.bind(this);

    if (this.editorElement.editor) {
      this._bindLinkButton();
    } else {
      this.editorElement.addEventListener("trix-initialize", this._onTrixInitialize);
    }
  }

  disconnect() {
    if (this.linkButton) {
      this.linkButton.removeEventListener("click", this._onLinkButtonClick, true);
    }
    if (this.editorElement) {
      this.editorElement.removeEventListener("trix-initialize", this._onTrixInitialize);
    }
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKey);
  }

  _onTrixInitialize() {
    this._bindLinkButton();
  }

  _bindLinkButton() {
    this.toolbar = this.editorElement.toolbarElement;
    this.linkButton = this.toolbar && this.toolbar.querySelector('[data-trix-attribute="href"]');
    if (this.linkButton) {
      this.linkButton.addEventListener("click", this._onLinkButtonClick, true);
    }
  }

  _onLinkButtonClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this._openDialog();
  }

  _openDialog() {
    const editor = this.editorElement.editor;
    if (!editor) return;

    this._savedRange = editor.getSelectedRange();
    const selectedText = this._selectedString(editor, this._savedRange);
    const existingHref = this._existingHref(editor, this._savedRange);

    if (this.hasTextTarget) {
      this.textTarget.value = selectedText || "";
    }
    if (this.hasUrlTarget) {
      this.urlTarget.value = existingHref || "";
      this._clearUrlInvalid();
    }
    if (this.hasRemoveTarget) {
      this.removeTarget.classList.toggle("d-none", !existingHref);
    }

    this.popoverTarget.classList.remove("d-none");
    this.element.classList.add("trix-link-dialog-open");

    document.addEventListener("click", this._onDocClick);
    document.addEventListener("keydown", this._onKey);

    const focusTarget = selectedText || existingHref ? this.urlTarget : this.textTarget;
    if (focusTarget) {
      requestAnimationFrame(() => focusTarget.focus());
    }
  }

  close(event) {
    if (event) event.preventDefault();
    this.popoverTarget.classList.add("d-none");
    this.element.classList.remove("trix-link-dialog-open");
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKey);
    this._savedRange = null;
  }

  apply(event) {
    if (event) event.preventDefault();

    const editor = this.editorElement.editor;
    if (!editor) return;

    const url = this.urlTarget.value.trim();
    const text = this.textTarget.value.trim();

    if (!url) {
      this._markUrlInvalid();
      return;
    }

    const normalizedUrl = this._normalizeUrl(url);
    if (!this._isValidUrl(normalizedUrl)) {
      this._markUrlInvalid();
      return;
    }
    this._clearUrlInvalid();

    this.editorElement.focus();

    if (this._savedRange) editor.setSelectedRange(this._savedRange);

    const range = editor.getSelectedRange();
    const isCollapsed = !range || range[0] === range[1];
    const start = range ? range[0] : 0;
    const currentSelectedText = isCollapsed ? "" : this._selectedString(editor, range);
    const insertText = text || (isCollapsed ? url : currentSelectedText);

    editor.recordUndoEntry("Add link");

    if (isCollapsed) {
      editor.insertString(insertText);
      editor.setSelectedRange([start, start + insertText.length]);
      editor.activateAttribute("href", normalizedUrl);
      editor.setSelectedRange([start + insertText.length, start + insertText.length]);
    } else if (text && text !== currentSelectedText) {
      editor.insertString(text);
      editor.setSelectedRange([start, start + text.length]);
      editor.activateAttribute("href", normalizedUrl);
      editor.setSelectedRange([start + text.length, start + text.length]);
    } else {
      editor.activateAttribute("href", normalizedUrl);
      editor.setSelectedRange([range[1], range[1]]);
    }

    this.close();
  }

  remove(event) {
    if (event) event.preventDefault();
    const editor = this.editorElement.editor;
    if (!editor) return;

    if (this._savedRange) editor.setSelectedRange(this._savedRange);
    editor.recordUndoEntry("Remove link");
    editor.deactivateAttribute("href");
    this.close();
    this.editorElement.focus();
  }

  _selectedString(editor, range) {
    if (!range || range[0] === range[1]) return "";
    try {
      const doc = editor.getDocument();
      return doc.getStringAtRange(range).replace(/\n$/, "");
    } catch (_e) {
      return "";
    }
  }

  _existingHref(editor, range) {
    if (!range) return "";
    try {
      const piece = editor.getDocument().getPieceAtPosition(range[0]);
      if (piece && piece.getAttribute) {
        return piece.getAttribute("href") || "";
      }
    } catch (_e) {
      return "";
    }
    return "";
  }

  _normalizeUrl(url) {
    if (/^(https?:|mailto:|tel:|\/|#)/i.test(url)) return url;
    if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(url)) return `mailto:${url}`;
    // Bare hostname like example.com or example.com/path — needs at least one dot
    // and a TLD-looking suffix to be treated as a URL.
    if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/.*)?$/i.test(url)) return `https://${url}`;
    return url;
  }

  _isValidUrl(url) {
    if (/^#./.test(url)) return true;
    if (/^\/[^\s]*$/.test(url)) return true;
    if (/^tel:\+?[\d\s().-]{3,}$/.test(url)) return true;
    if (/^mailto:[\w.+-]+@[\w.-]+\.\w+$/i.test(url)) return true;
    if (/^https?:\/\//i.test(url)) {
      try {
        const parsed = new URL(url);
        if (!parsed.hostname || !parsed.hostname.includes(".")) return false;
        if (!/^[a-z0-9.-]+$/i.test(parsed.hostname)) return false;
        return true;
      } catch (_e) {
        return false;
      }
    }
    return false;
  }

  _markUrlInvalid() {
    this.urlTarget.classList.add("is-invalid");
    this.urlTarget.focus();
    this.urlTarget.select();
  }

  _clearUrlInvalid() {
    this.urlTarget.classList.remove("is-invalid");
  }

  clearInvalid() {
    this._clearUrlInvalid();
  }

  _onDocClick(event) {
    if (this.popoverTarget.contains(event.target)) return;
    if (this.linkButton && this.linkButton.contains(event.target)) return;
    this.close();
  }

  _onKey(event) {
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "Enter" && this.popoverTarget.contains(event.target)) {
      event.preventDefault();
      this.apply();
    }
  }
}
